import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ClipboardList, ImageIcon } from "lucide-react";
import { CatalogBrowser } from "@/components/master-catalog/CatalogBrowser";
import { adminCatalogApi } from "@/components/master-catalog/mcApi";

const Stat = ({ label, v, id }) => (
  <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{label}</div><div className="mc-num text-lg" data-testid={`mc-stat-${id}`}>{v}</div></div>
);

function PhotoReview() {
  const [p, setP] = useState(null);
  const load = () => api.get("/master-catalog/photos").then((r) => setP(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!p?.scan?.running) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [p]);
  const scan = async () => {
    const { data } = await api.post("/master-catalog/photos/scan");
    toast.success(data.started ? `Scanning ${data.total} photos` : "All photos already scanned");
    load();
  };
  if (!p) return null;
  return (
    <div className="space-y-2 border-t border-[#0D1B2A]/10 pt-3" data-testid="mc-photo-review">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-600 flex items-center gap-2"><ImageIcon className="w-4 h-4" />
          <span className="mc-num">{p.scanned}/{p.distinct_photos}</span> photos measured
          {p.scan.running && <span className="text-amber-700 mc-num">· scanning {p.scan.done}/{p.scan.total}</span>}
          · <span className="mc-num" data-testid="mc-lowres-count">{p.low_res_count}</span> designs use a photo under {p.low_res_threshold_px}px
          · <span className="mc-num">{p.designs_without_photo}</span> without photo
        </div>
        <Button variant="outline" size="sm" onClick={scan} disabled={p.scan.running} data-testid="mc-photo-scan-btn">Scan photos</Button>
      </div>
      {p.low_res.length > 0 && (
        <div className="max-h-48 overflow-y-auto text-[11px] mc-num" data-testid="mc-lowres-list">
          {p.low_res.map((x) => (
            <div key={x.design_key} className="border-t border-[#0D1B2A]/5 py-1">{x.design_key} · {x.width}×{x.height}px · source {x.style_no}{x.jewel_code ? ` / ${x.jewel_code}` : ""} — replace</div>
          ))}
        </div>
      )}
    </div>
  );
}

function DataReview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/master-catalog/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-xs text-slate-400">Loading…</div>;
  return (
    <div className="luxe-card p-4 space-y-3" data-testid="mc-data-review">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <Stat label="Eligible rows" v={s.eligible_rows} id="eligible" />
        <Stat label="Unique designs" v={s.unique_designs} id="unique" />
        <Stat label="Duplicate groups" v={s.duplicate_groups} id="dup" />
        <Stat label="Multi-variant" v={s.multi_variant_designs} id="multi" />
        <Stat label="Ready designs" v={s.ready_designs} id="ready" />
        <Stat label="Non-gold excluded" v={s.non_gold_count} id="nongold" />
        <Stat label="Unparsed suffix" v={s.unparsed_suffix_count} id="unparsed" />
        <Stat label="Data notes" v={s.conflict_count} id="conflicts" />
      </div>
      <div className="text-xs text-slate-500">By source: {Object.entries(s.by_kind).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>
      <div className="max-h-56 overflow-y-auto text-[11px] mc-num" data-testid="mc-conflict-list">
        {s.conflicts.slice(0, 150).map((c) => (
          <div key={`${c.design_key}-${c.type}-${c.variant || ""}-${c.style_no || ""}`} className="border-t border-[#0D1B2A]/5 py-1">{c.design_key} · {c.type} {c.variant || ""} {c.min != null ? `${c.min}–${c.max} g` : ""} {c.total_ct_values ? c.total_ct_values.join(" / ") + " ct" : ""} {c.style_no || ""}</div>
        ))}
      </div>
      <PhotoReview />
    </div>
  );
}

export default function MasterCatalogBrowse() {
  const capi = useMemo(() => adminCatalogApi(), []);
  const { user } = useAuth();
  const [review, setReview] = useState(false);
  const isMaster = user?.role === "master_admin";
  return (
    <div className="space-y-5" data-testid="mc-admin-browse">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Link to="/master-catalog" className="text-xs text-slate-500 flex items-center gap-1" data-testid="mc-back-home"><ArrowLeft className="w-3.5 h-3.5" /> Master Catalog</Link>
          <h1 className="font-serif text-4xl text-[#0D1B2A] mt-1 tracking-wide">Master Catalog</h1>
        </div>
        {isMaster && (
          <Button variant="outline" size="sm" onClick={() => setReview(!review)} data-testid="mc-data-review-toggle">
            <ClipboardList className="w-4 h-4 mr-2" /> {review ? "Hide data review" : "Data review (HO)"}
          </Button>
        )}
      </div>
      {isMaster && review && <DataReview />}
      <CatalogBrowser api={capi} />
    </div>
  );
}
